// Normalize user-supplied social handles / URLs into safe, canonical
// destinations. Shop owners and barbers paste these into the dashboard in
// every shape imaginable — "@handle", "instagram.com/handle", a full URL, or
// a typo like "http://instagram/foo" that is MISSING the .com. That last shape
// is the dangerous one: the browser treats "instagram" as a hostname, DNS
// fails or resolves somewhere unintended, and the user can land on a
// typo-squatter domain from a link that looked official on your listing.
//
// These run at render time, so bad data already sitting in the database is
// fixed the moment a page loads — no migration or backfill needed. Anything
// that can't be parsed into a safe handle returns null, and the caller simply
// doesn't render that icon. A missing link beats a wrong one.
//
// ⚠️ NEVER use these on booking links.
// They deliberately strip query strings, and BOOTH's booking links carry
// `?src=` — the parameter the whole booking-channel attribution feature runs
// on (see components/booking/channels.tsx). Sanitizing a booking URL would
// silently reset every visit to "direct". These are for the social profile
// fields only: instagram_url, tiktok_url, youtube_url.

interface SocialDomainOptions {
  /** The canonical domain (without protocol or www), e.g. "instagram.com". */
  domain: string
  /** Prefix applied to the parsed handle, e.g. "@" for TikTok and YouTube. */
  handlePrefix?: string
  /**
   * When true, a remainder containing "/" is treated as a real path
   * (youtube.com/channel/UC...) and emitted verbatim instead of being
   * prefixed as a handle.
   */
  preserveNestedPaths?: boolean
}

function normalizeSocialUrl(
  input: string | null | undefined,
  { domain, handlePrefix = "", preserveNestedPaths = false }: SocialDomainOptions,
): string | null {
  if (!input) return null
  let s = input.trim()
  if (!s) return null

  // Strip any protocol. This is what defuses `http://instagram/foo`: the
  // protocol goes here, then the bare "instagram" is stripped below.
  s = s.replace(/^(https?:)?\/\//i, "")
  s = s.replace(/^www\./i, "")

  // Strip the platform's own domain, with or without .com:
  //   "instagram/foo"     -> "foo"
  //   "instagram.com/foo" -> "foo"
  //   "instagram.com"     -> ""
  const base = domain.split(".")[0]
  s = s.replace(new RegExp(`^${base}(\\.com)?/?`, "i"), "")

  // Leading @ comes off; handlePrefix puts it back where the platform wants it.
  s = s.replace(/^@+/, "")

  // Drop query, fragment, trailing slashes.
  s = s.split("?")[0].split("#")[0].replace(/\/+$/, "")

  if (!s) return null

  // Safety net: if something URL-shaped or whitespace survived the strip, bail
  // rather than emit a link that points somewhere unintended.
  if (s.includes("://") || /\s/.test(s)) return null

  if (preserveNestedPaths && s.includes("/")) {
    return `https://www.${domain}/${s}`
  }
  return `https://www.${domain}/${handlePrefix}${s}`
}

export function normalizeInstagramUrl(input: string | null | undefined): string | null {
  return normalizeSocialUrl(input, { domain: "instagram.com" })
}

export function normalizeTikTokUrl(input: string | null | undefined): string | null {
  return normalizeSocialUrl(input, { domain: "tiktok.com", handlePrefix: "@" })
}

export function normalizeYouTubeUrl(input: string | null | undefined): string | null {
  return normalizeSocialUrl(input, {
    domain: "youtube.com",
    handlePrefix: "@",
    preserveNestedPaths: true,
  })
}

export function normalizeXUrl(input: string | null | undefined): string | null {
  if (!input) return null
  let s = input.trim()
  if (!s) return null
  s = s.replace(/^(https?:)?\/\//i, "")
  s = s.replace(/^www\./i, "")
  s = s.replace(/^(twitter|x)\.com\/?/i, "")
  s = s.replace(/^@+/, "")
  s = s.split("?")[0].split("#")[0].replace(/\/+$/, "")
  if (!s || s.includes("://") || /\s/.test(s)) return null
  return `https://x.com/${s}`
}

export function normalizeFacebookUrl(input: string | null | undefined): string | null {
  return normalizeSocialUrl(input, { domain: "facebook.com", preserveNestedPaths: true })
}

/** Pull the trailing handle from a normalized social profile URL. */
export function socialHandleFromUrl(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const path = new URL(url).pathname.replace(/\/+$/, "")
    const segment = path.split("/").filter(Boolean).pop()
    if (!segment) return null
    return segment.replace(/^@+/, "")
  } catch {
    return null
  }
}
